import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
interface Feature {
  id: number;
  title: string;
  description: string;
  image: string;
  modalImage?: string;
  userInsight: string;
  solution: string;
  userOutcome: string;
  userOutcomeLabel?: string;
  businessOutcome: string;
  businessOutcomeLabel?: string;
  quote: string;
}
interface FeatureModalProps {
  feature: Feature | null;
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}
export function FeatureModal({
  feature,
  isOpen,
  onClose,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext
}: FeatureModalProps) {
  if (!feature) return null;
  return <AnimatePresence data-id="element-63">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50" onClick={onClose} data-id="element-64" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-65">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} className="relative w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto" data-id="element-66">
              {/* Close Button */}
              <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 backdrop-blur-sm shadow-lg hover:bg-white transition-colors" aria-label="Close modal" data-id="element-67">
                <X className="w-5 h-5 text-slate-700" data-id="element-68" />
              </button>

              {/* Navigation Arrows - bottom center */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3" data-id="element-69">
                {hasPrevious && <button onClick={onPrevious} className="p-2.5 rounded-full bg-white/90 backdrop-blur-sm shadow-lg hover:bg-white transition-all hover:scale-110" aria-label="Previous feature" data-id="element-70">
                    <ChevronLeft className="w-5 h-5 text-slate-700" data-id="element-71" />
                  </button>}
                {hasNext && <button onClick={onNext} className="p-2.5 rounded-full bg-white/90 backdrop-blur-sm shadow-lg hover:bg-white transition-all hover:scale-110" aria-label="Next feature" data-id="element-72">
                    <ChevronRight className="w-5 h-5 text-slate-700" data-id="element-73" />
                  </button>}
              </div>

              {/* Two-column layout */}
              <div className="overflow-y-auto max-h-[90vh]" data-id="element-74">
                <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-75">
                  {/* Left column - Sticky image */}
                  <div className="lg:sticky lg:top-0 lg:self-start lg:max-h-[90vh]" data-id="element-76">
                    <img src={feature.modalImage || feature.image} alt={feature.title} className="w-full h-auto lg:h-auto lg:min-h-0 object-contain" data-id="element-77" />
                  </div>

                  {/* Right column - Scrollable text content */}
                  <div className="p-8 lg:p-10 space-y-8 pb-20" data-id="element-78">
                    {/* Header */}
                    <div data-id="element-79">
                      <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" data-id="element-80">
                        {feature.title}
                      </h2>
                      <p className="text-lg text-slate-600 leading-relaxed" data-id="element-81">
                        {feature.description}
                      </p>
                    </div>

                    {/* Quote */}
                    {feature.quote && <div className="relative py-6 px-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl border-l-4 border-blue-500" data-id="element-82">
                        <svg className="absolute top-4 left-6 w-8 h-8 text-blue-200 opacity-50" fill="currentColor" viewBox="0 0 32 32" data-id="element-83">
                          <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-1.1.9-2 2-2V8zm12 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-1.1.9-2 2-2V8z" data-id="element-84" />
                        </svg>
                        <p className="text-lg text-slate-700 italic leading-relaxed pl-8" data-id="element-85">
                          {feature.quote}
                        </p>
                      </div>}

                    {/* Details - Single column stack */}
                    <div className="space-y-6" data-id="element-86">
                      <div className="space-y-2" data-id="element-87">
                        <h4 className="text-lg font-semibold text-blue-600" data-id="element-88">
                          User insight:
                        </h4>
                        <p className="text-slate-600 leading-relaxed" data-id="element-89">
                          {feature.userInsight}
                        </p>
                      </div>

                      <div className="space-y-2" data-id="element-90">
                        <h4 className="text-lg font-semibold text-blue-600" data-id="element-91">
                          Solution:
                        </h4>
                        <p className="text-slate-600 leading-relaxed" data-id="element-92">
                          {feature.solution}
                        </p>
                      </div>

                      <div className="space-y-2" data-id="element-93">
                        <h4 className="text-lg font-semibold text-blue-600" data-id="element-94">
                          {feature.userOutcomeLabel || 'User outcome:'}
                        </h4>
                        <p className="text-slate-600 leading-relaxed" data-id="element-95">
                          {feature.userOutcome}
                        </p>
                      </div>

                      <div className="space-y-2" data-id="element-96">
                        <h4 className="text-lg font-semibold text-blue-600" data-id="element-97">
                          {feature.businessOutcomeLabel || 'Business outcome:'}
                        </h4>
                        <p className="text-slate-600 leading-relaxed" data-id="element-98">
                          {feature.businessOutcome}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}